import { useEffect, useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useOmniAuth } from '@omni/auth'
import { parseEveryone, parseHits, parseMyDay, searchWords, type EveryoneDay, type MyDay, type SearchHit } from './shape'

// My day and the one search, read from Nateva's shared database through the SHELL SESSION (the same client
// the Programme and Team pages use). Three database functions do all the work and apply each app's rules,
// so a person only ever gets what they may see (nateva-platform migrations 0100 and 0101):
//   core.my_day(p_scope)   what is theirs today, this week and this month
//   core.everyone_day()    the same by person, for leadership and admins only
//   core.search_all(q)     people, blocks, deals, agreements, notes, plots, reports, projects
// Nothing here writes. Search words are not stored.

type Rpc = PromiseLike<{ data: unknown; error: { message: string; code?: string } | null }>
type AnyDb = { schema(s: string): { rpc(fn: string, args?: Record<string, unknown>): Rpc } }

function useCore() {
  const { getClient } = useOmniAuth()
  return useMemo(() => (getClient('crm') as unknown as AnyDb).schema('core'), [getClient])
}

async function call(core: ReturnType<typeof useCore>, fn: string, args?: Record<string, unknown>): Promise<unknown> {
  const r = await core.rpc(fn, args)
  if (r.error) throw new Error(r.error.message)
  return r.data
}

export function useMyDay(scope: 'mine' | 'all') {
  const core = useCore()
  return useQuery<MyDay | null>({
    queryKey: ['nateva', 'my-day', scope],
    queryFn: async () => parseMyDay(await call(core, 'my_day', { p_scope: scope })),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  })
}

export function useEveryoneDay(enabled: boolean) {
  const core = useCore()
  return useQuery<EveryoneDay | null>({
    queryKey: ['nateva', 'everyone-day'],
    queryFn: async () => parseEveryone(await call(core, 'everyone_day')),
    enabled,
    staleTime: 60_000,
  })
}

/** The search box's words after a short pause, so a database call is not made for every key. */
function useSettled(value: string, ms = 220): string {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

export type SearchState = { hits: SearchHit[]; loading: boolean; failed: boolean }

/** One search across every app. Exported to the front page search box (surface-apps `useSearchAll`). */
export function useSearchAll(q: string): SearchState {
  const core = useCore()
  const words = searchWords(useSettled(q))
  const live = searchWords(q)
  const query = useQuery<SearchHit[]>({
    queryKey: ['nateva', 'search', words],
    queryFn: async () => parseHits(await call(core, 'search_all', { q: words, p_limit: 30, p_per_kind: 6 })),
    enabled: words !== null,
    staleTime: 30_000,
    retry: false,
    placeholderData: keepPreviousData,
  })
  if (!live) return { hits: [], loading: false, failed: false }
  return { hits: query.data ?? [], loading: query.isFetching || words !== live, failed: query.isError }
}

/** The same search, once, for the Ctrl+K window (it keeps its own pause and abort). */
export async function searchAllOnce(client: unknown, q: string, signal?: AbortSignal): Promise<SearchHit[]> {
  const words = searchWords(q)
  if (!words) return []
  const core = (client as AnyDb).schema('core')
  const r = await core.rpc('search_all', { q: words, p_limit: 30, p_per_kind: 6 })
  if (signal?.aborted) return []
  if (r.error) throw new Error(r.error.message)
  return parseHits(r.data)
}
